import {templatesApi} from '@dv5/api'
import {Template, TemplateItemParams, TemplateType, useAppDispatch} from '@dv5/domain'
import React, {FC, useEffect, useRef, useState} from 'react'
import {ErrorDisplayProcessor} from "../ErrorDisplayProcessor"
import {templateService} from "../service/templateService"
import {TemplateListContent} from "./TemplateListContent"
import {TemplateParamDialog} from "./TemplateParamDialog"
import './UseTemplate.less'

interface Props {
    templateTypes: TemplateType[]
    onSelect?: (template: Template, params: TemplateItemParams) => void
    onCancel: () => void
    mode?: 'selection' | 'manager'
    initialType?: TemplateType
    initialTemplateId?: string
}

export const UseTemplate: FC<Props> = ({
                                            templateTypes,
                                            onSelect,
                                            onCancel,
                                            mode = 'selection',
                                            initialType,
                                            initialTemplateId,
                                        }) => {
    const dispatch = useAppDispatch()
    const errorProcessor = new ErrorDisplayProcessor(dispatch)
    const dialogRef = useRef<HTMLDivElement>(null)
    const [selectedType, setSelectedType] = useState<TemplateType>(initialType && templateTypes.includes(initialType) ? initialType : templateTypes[0])
    const [templates, setTemplates] = useState<Array<Template>>([])
    const [isLoading, setIsLoading] = useState(templateTypes.length > 0)
    const [pendingParamsTemplate, setPendingParamsTemplate] = useState<Template | null>(null)

    useEffect(() => {
        if (templateTypes.length === 0) {
            setIsLoading(false)
            return
        }
        templatesApi.getTemplatesByType(
            selectedType,
            errorProcessor,
            (loadedTemplates) => {
                setTemplates(loadedTemplates)
                setIsLoading(false)
            }
        ).catch(() => {
            setIsLoading(false)
        })
    }, [selectedType, templateTypes.length])

    useEffect(() => {
        if (initialTemplateId && mode === 'manager') {
            // Wait for templates to load, then scroll to the selected template
            if (templates.length > 0) {
                const element = document.getElementById(`template-item-${initialTemplateId}`)
                if (element) {
                    setTimeout(() => {
                        element.scrollIntoView({behavior: 'smooth', block: 'center'})
                    }, 100)
                }
            }
        }
    }, [initialTemplateId, mode, templates])

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
            onCancel()
        }
    }

    const handleTemplateClick = (template: Template, params: TemplateItemParams) => {
        if (mode === 'selection' && onSelect) {
            if (template.settings) {
                setPendingParamsTemplate(template)
            } else {
                onSelect(template, params)
            }
        }
    }

    const handleParamConfirm = (params: TemplateItemParams) => {
        if (pendingParamsTemplate && onSelect) {
            onSelect(pendingParamsTemplate, params)
        }
        setPendingParamsTemplate(null)
    }

    const handleParamCancel = () => {
        setPendingParamsTemplate(null)
    }

    const handleCancel = () => {
        onCancel()
    }

    const handleDeleteTemplate = (templateId: string) => {
        templatesApi.removeTemplate(
            templateId,
            errorProcessor,
            (removedId) => {
                setTemplates(templates.filter(t => t.id !== removedId))
            }
        ).catch(console.error)
    }

    const handleRenameTemplate = (templateId: string, newTitle: string) => {
        templatesApi.renameTemplate(
            templateId,
            newTitle,
            errorProcessor,
            (updatedTemplate) => {
                setTemplates(templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t))
            }
        ).catch(console.error)
    }

    const handleTemplateUpdated = (updatedTemplate: Template) => {
        setTemplates(templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t))
    }

    const renderTypeSwitcher = () => (
        <div className="type-switcher">
            {templateTypes.map((type) => (
                <button
                    key={type}
                    className={`type-button ${selectedType === type ? 'active' : ''}`}
                    onClick={() => setSelectedType(type)}
                >
                    {templateService.getTitle(type)}
                </button>
            ))}
        </div>
    )

    const renderEmptyMessage = () => (
        <div className="template-list-container">
            <div className="empty-message">No "{templateService.getTitle(selectedType)}" templates available</div>
        </div>
    )

    if (mode === 'manager') {
        return (
            <div className="use-template-manager">
                {templateTypes.length === 0 ? (
                    renderEmptyMessage()
                ) : (
                    <>
                        {renderTypeSwitcher()}
                        <TemplateListContent
                            templates={templates}
                            isLoading={isLoading}
                            mode={mode}
                            type={selectedType}
                            onSelect={handleTemplateClick}
                            onDelete={handleDeleteTemplate}
                            onRename={handleRenameTemplate}
                            onCancel={handleCancel}
                            onTemplateUpdated={handleTemplateUpdated}
                            selectedTemplateId={initialTemplateId}
                        />
                    </>
                )}
            </div>
        )
    }

    return (
        <div className="use-template-dialog-overlay" onClick={handleOverlayClick}>
            <div className="use-template-dialog" ref={dialogRef}>
                {pendingParamsTemplate ? (
                    <TemplateParamDialog
                        template={pendingParamsTemplate}
                        onConfirm={handleParamConfirm}
                        onCancel={handleParamCancel}
                    />
                ) : (
                    <>
                        <h3>Select Template</h3>
                        {templateTypes.length === 0 ? (
                            renderEmptyMessage()
                        ) : (
                            <>
                                {renderTypeSwitcher()}
                                <TemplateListContent
                                    templates={templates}
                                    isLoading={isLoading}
                                    mode={mode}
                                    type={selectedType}
                                    onSelect={handleTemplateClick}
                                    onDelete={handleDeleteTemplate}
                                    onRename={handleRenameTemplate}
                                    onCancel={handleCancel}
                                    onTemplateUpdated={handleTemplateUpdated}
                                    selectedTemplateId={undefined}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
